// This file used declaration merging, so we need to use interfaces for that.
/* eslint-disable @typescript-eslint/consistent-type-definitions */

import { useEffect } from 'react';

import { ThemeProvider, StyledEngineProvider } from '@mui/material/styles';
import { AdapterLuxon } from '@mui/x-date-pickers/AdapterLuxon';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { LocalizationProvider as LocalizationProviderPro } from '@mui/x-date-pickers-pro';
import { LicenseInfo } from '@mui/x-license-pro';
import { ReactQueryDevtools } from '@tanstack/react-query-devtools';
import { useFlags } from 'launchdarkly-react-client-sdk';
import { useDispatch } from 'react-redux';
import { Switch, Route, useLocation, useHistory } from 'react-router-dom';
import { ReactFlowProvider } from 'reactflow';

import { useCustomAuth } from 'auth/AuthContext';
import ConditionalSplitPage from 'domains/campaigns/pages/CurationPages/ConditionalSplitPage';
import CurationLandingPage from 'domains/campaigns/pages/CurationPages/CurationLandingPage';
import CustomPage from 'domains/campaigns/pages/CurationPages/CustomPage';
import SingleSendPage from 'domains/campaigns/pages/CurationPages/SingleSendPage';
import TimeDelayPage from 'domains/campaigns/pages/CurationPages/TimeDelayPage';
import CurationsPage from 'domains/campaigns/pages/CurationsPage';
import CampaignDetailsPageWrapper from 'domains/campaigns/pages/CurationsPage/CampaignDetailsPage';
import { CampaignChannels } from 'domains/campaigns/types';
import { AccessCentreRouteGuard } from 'domains/core/components/AccessCentreRouteGuards';
import Background from 'domains/core/components/Background';
import NavigationPanel from 'domains/core/components/NavigationPanel';
import RouteGuard from 'domains/core/components/RouteGuard';
import NestedSegmentCreatePage from 'domains/segments/pages/NestedSegmentCreatePage';
import SegmentCreatePage from 'domains/segments/pages/SegmentCreatePage';
import SegmentsPage from 'domains/segments/pages/SegmentsPage';
import ValueCalculatorCreatePage from 'domains/value/pages/ValueCalculatorCreatePage';
import ValueCalculatorDetailsPage from 'domains/value/pages/ValueCalculatorDetailsPage';
import ValueCalculatorPage from 'domains/value/pages/ValueCalculatorPage';
import { initMixpanel, identifyMixpanelUser } from 'helpers/initMixpanel/initMixpanel';
import { injectSatismeter } from 'helpers/injectSatismeter/injectSatismeter';
import { useAccessCentrePermissions } from 'hooks/useAccessCentrePermissions';
import useConfig from 'hooks/useConfig';
import { useContentOnlyPermission } from 'hooks/useContentOnlyPermission';
import AIGuardrailsPage from 'pages/AIGuardrailsPage';
import SettingsBrandRoute from 'pages/BrandSetupPage/SettingsBrandRoute';
import ContentViewScreen from 'pages/ContentPage/ContentViewScreen';
import ContentSmsBuilderPage from 'pages/ContentSmsBuilderPage';
import ContentVoiceBuilderPage from 'pages/ContentVoiceBuilderPage';
import ContentWhatsAppBuilderPage from 'pages/ContentWhatsAppBuilderPage/ContentWhatsAppBuilderPage';
import CustomerSupportPage from 'pages/CustomerSupportPage';
import ImportTemplatePage from 'pages/ImportTemplatePage/ImportTemplatePage';
import InnovaccerLogin from 'pages/InnovaccerLogin';
import SettingsRolesRoute from 'pages/RolesPage/SettingsRolesRoute';
import UsersPage from 'pages/UsersPage';

import { AccessCentrePermissionKey, AccessCentreRouteAccessMode, URLPaths } from './models/enums';
import ContactsPage from './pages/ContactsPage';
import ContentDirectMailBuilderPage from './pages/ContentDirectMailBuilderPage';
import ContentEmailBuilderPage from './pages/ContentEmailBuilderPage';
import ContentPage from './pages/ContentPage';
import ContentPageBuilderPage from './pages/ContentPageBuilderPage';
import FinishSignupPage from './pages/FinishSignupPage';
import HomePage from './pages/HomePage';
import InsightsPage from './pages/InsightsPage';
import LoginPage from './pages/LoginPage';
import UnsubscribeLandingPage from './pages/UnsubscribeLandingPage';
import ViewEmailPage from './pages/ViewEmailPage';
import { minimizeLeftNavPanel } from './state/appVisual/appVisualActions';
import theme from './theme';

import './App.css';

// Needed for type support - https://mui.com/x/react-date-pickers/getting-started/#typescript
import type {} from '@mui/x-date-pickers-pro/themeAugmentation';
import type {} from '@mui/x-date-pickers/themeAugmentation';

LicenseInfo.setLicenseKey(
    'dd2075388d0abb190a84af839b8565eaTz05NjI4NyxFPTE3NTUzNzc1NjkwMDAsUz1wcm8sTE09c3Vic2NyaXB0aW9uLFBWPWluaXRpYWwsS1Y9Mg=='
);

interface OtherOptions {
    divider: string;
    outlineBorder: string;
    standardInputLine: string;
    backdropOverlay: string;
    ratingActive: string;
    snackbarBackground: string;
    actionBarBackground: string;
    tableBackground: string;
    tableHover: string;
    stone: string;
    lightMint: string;
    cumin: string;
    paloma: string;
    water: string;
    lime: string;
    mint: string;
    garlic: string;
    radish: string;
    mineral: string;
    newPanelBackground: string;
}
// 2. Notify TypeScript about the new color in the palette
declare module '@mui/material/styles' {
    interface SimplePaletteColorOptions {
        containedHoverBackground?: string;
        outlinedHoverBackground?: string;
        outlinedRestingBackground?: string;
        outlinedRestingBorder?: string;
        content?: string;
        background?: string;
    }
    interface Palette {
        other: OtherOptions;
    }
    interface PaletteOptions {
        other: OtherOptions;
    }
    interface SimplePaletteColorOptions {
        content?: string;
    }
    interface PaletteColor {
        content?: string;
    }
}
declare module '@mui/material/styles' {
    interface TypographyVariants {
        avatarInitials: React.CSSProperties;
        buttonLarge: React.CSSProperties;
        buttonMedium: React.CSSProperties;
        buttonSmall: React.CSSProperties;
        inputLabel: React.CSSProperties;
        inputText: React.CSSProperties;
        helperText: React.CSSProperties;
        tooltip: React.CSSProperties;
    }
    // allow configuration using `createTheme`
    interface TypographyVariantsOptions {
        avatarInitials?: React.CSSProperties;
        buttonLarge?: React.CSSProperties;
        buttonMedium?: React.CSSProperties;
        buttonSmall?: React.CSSProperties;
        inputLabel?: React.CSSProperties;
        inputText?: React.CSSProperties;
        helperText?: React.CSSProperties;
        tooltip?: React.CSSProperties;
    }
}
declare module '@mui/material/Typography' {
    interface TypographyPropsVariantOverrides {
        buttonLarge: true;
        buttonMedium: true;
        buttonSmall: true;
        avatarInitials: true;
        inputLabel: true;
        inputText: true;
        helperText: true;
        tooltip: true;
    }
}

/**
 *
 * loggedOutPaths checks if the user is in an unauthenticated area of the app
 * This can be either when the user is on the login page or on the Innovaccer Login Route
 * The reason we perform a regex is because this path would have the tenant_id and auth_code appended
 * so we match the beginning of the path to check if the user is on the URLPaths.INNOVACCER_LOGIN path
 * and add the class accordingly.
 */
const getAppBodyClassName = (path: string, isAuthenticated: boolean) => {
    let className = 'app-body';
    const loggedOutPaths = path === '' || ('/' + path).search(URLPaths.INNOVACCER_LOGIN) === 0;
    if (path === '' && isAuthenticated) {
        className += ' app-body-home-page';
    } else if (loggedOutPaths && !isAuthenticated) {
        className += ' app-body-login-page';
    }
    return className;
};

const knownUserHasNoRoles = (user: any, auth0AudienceEndpoint: string) => {
    const roleCount = user?.[auth0AudienceEndpoint]?.length;
    return roleCount === 0;
};

const App = () => {
    const dispatch = useDispatch();
    const history = useHistory();
    const { isAuthenticated, isLoading, loginWithRedirect, user } = useCustomAuth();
    const {
        config: { AUTH0_AUDIENCE_ENDPOINT, SATISMETER_WRITE_KEY, SATISMETER_ENDPOINT, MIXPANEL_TOKEN },
    } = useConfig();
    const { hasContentOnly, isLoading: isContentOnlyLoading } = useContentOnlyPermission();
    const {
        audienceBuilderNested: hasAudienceBuilderNested,
        aiBrandTools: hasAiBrandToolsFlag,
        customerSupport: hasCustomerSupportFlag,
        aiGuardrailsAdminPanel: hasAiGuardrailsAdminPanelFlag,
        accessCenter: hasAccessCenterFlag,
    } = useFlags();

    const { canRegisterAiBrandVoice, isAccessCentrePermissionsLoading } = useAccessCentrePermissions();

    const brandSettingsRouteAllowed =
        hasAccessCenterFlag !== true
            ? !!hasAiBrandToolsFlag
            : !!hasAccessCenterFlag && !!hasAiBrandToolsFlag && canRegisterAiBrandVoice;

    const brandSettingsRouteLoading =
        hasAccessCenterFlag === undefined ||
        hasAiBrandToolsFlag === undefined ||
        (hasAccessCenterFlag === true && isAccessCentrePermissionsLoading);

    /*
        If the user tries to access any page other than the homepage(which renders the login button if they are not authenticated),
        the unsubscribe page, which does not require authentication,
        redirect them to the auth0 login page then back to their original destination once authenticated.
        Adding to the above paths, we also have another unauthenticated path now ie Innovaccer Login Path
        We do not want to perform a redirect to Auth0 from this path either, hence we check if the history.location.pathname.search(URLPaths.INNOVACCER_LOGIN) < 0 since it returns a -1 in case the string is not matched
    */
    useEffect(() => {
        if (
            !isLoading &&
            !isAuthenticated &&
            history.location.pathname !== URLPaths.HOME_PAGE &&
            history.location.pathname !== URLPaths.UNSUBSCRIBE &&
            history.location.pathname !== URLPaths.VIEW_EMAIL &&
            history.location.pathname.search(URLPaths.INNOVACCER_LOGIN) < 0
        ) {
            loginWithRedirect({
                appState: {
                    returnTo: history.location.pathname,
                },
            });
        }
    }, [history.location.pathname, isAuthenticated, isLoading, loginWithRedirect]);

    /**
     * Analytics Integration
     * 1. Satismeter integration
     * Link : https://app.satismeter.com/install-instructions/6613de55ad1b6ce6094992f0?installAuthKey=ts11YBj5i32HzMA4UCHjMFbNatft1BB7
     */
    useEffect(() => {
        if (!isAuthenticated || !user) return;
        const { name, email } = user;
        if (SATISMETER_ENDPOINT && SATISMETER_WRITE_KEY) {
            injectSatismeter(SATISMETER_ENDPOINT);

            (window as any).satismeter({
                writeKey: SATISMETER_WRITE_KEY,
                userId: email,
                traits: {
                    name,
                    email,
                },
            });
        }
    }, [user, isAuthenticated, SATISMETER_ENDPOINT, SATISMETER_WRITE_KEY]);

    /**
     * Analytics Integration
     * 2. Mixpanel integration
     */
    useEffect(() => {
        if (MIXPANEL_TOKEN) {
            initMixpanel(MIXPANEL_TOKEN);
        }
    }, [MIXPANEL_TOKEN]);

    useEffect(() => {
        if (!isAuthenticated || !user || !MIXPANEL_TOKEN) return;
        const { name, email } = user;
        identifyMixpanelUser(email, {
            name,
            email,
        });
    }, [user, isAuthenticated, MIXPANEL_TOKEN]);

    // The following paths need the left nav panel minimized
    const path = useLocation().pathname.substring(useLocation().pathname.indexOf('/') + 1);

    if (
        (path === '' && !isAuthenticated) ||
        path === URLPaths.CONTENT_CREATE_EMAIL ||
        path === URLPaths.CONTENT_CREATE_PAGE
    ) {
        dispatch(minimizeLeftNavPanel(false));
    }

    // If the user has not been assigned a role yet, redirect them to the FinishSignupPage.
    const userHasNoRoles = knownUserHasNoRoles(user, AUTH0_AUDIENCE_ENDPOINT);
    if (userHasNoRoles) {
        return (
            <StyledEngineProvider injectFirst>
                <ThemeProvider theme={theme}>
                    <div id="app-content">
                        <Route path="/" component={FinishSignupPage} exact />
                    </div>
                </ThemeProvider>
            </StyledEngineProvider>
        );
    }

    return (
        <StyledEngineProvider injectFirst>
            <ThemeProvider theme={theme}>
                <LocalizationProvider dateAdapter={AdapterLuxon}>
                    <LocalizationProviderPro dateAdapter={AdapterLuxon}>
                        <ReactFlowProvider>
                            <Switch>
                                <Route path={URLPaths.UNSUBSCRIBE} component={UnsubscribeLandingPage} exact />
                                <Route path={URLPaths.VIEW_EMAIL} component={ViewEmailPage} exact />
                                <Route path="*">
                                    <div className={getAppBodyClassName(path, isAuthenticated)}>
                                        <a href="#main-content" className="a11y-skip-link">
                                            Skip to main content
                                        </a>
                                        <NavigationPanel />
                                        <Background id="main-content">
                                            <Switch>
                                                {!isAuthenticated && (
                                                    <Route path={URLPaths.HOME_PAGE} component={LoginPage} exact />
                                                )}
                                                <Route
                                                    path={`${URLPaths.INNOVACCER_LOGIN}/:tenantId/:authCode`}
                                                    component={InnovaccerLogin}
                                                    exact
                                                />
                                                {isAuthenticated && (
                                                    <Route path={URLPaths.HOME_PAGE} component={HomePage} exact />
                                                )}
                                                {isAuthenticated && (
                                                    <Route path={URLPaths.CURATIONS_LANDING} exact>
                                                        <RouteGuard
                                                            hasPermission={!hasContentOnly}
                                                            isLoading={isContentOnlyLoading}
                                                        >
                                                            <AccessCentreRouteGuard
                                                                accessMode={
                                                                    AccessCentreRouteAccessMode.AllowLegacyUnlessGated
                                                                }
                                                                permission={
                                                                    AccessCentrePermissionKey.CurationsManageDrafts
                                                                }
                                                            >
                                                                <CurationLandingPage />
                                                            </AccessCentreRouteGuard>
                                                        </RouteGuard>
                                                    </Route>
                                                )}
                                                {isAuthenticated && (
                                                    <Route path={`${URLPaths.CURATIONS_CUSTOM}/:campaignId?`}>
                                                        <RouteGuard
                                                            hasPermission={!hasContentOnly}
                                                            isLoading={isContentOnlyLoading}
                                                        >
                                                            <AccessCentreRouteGuard
                                                                accessMode={
                                                                    AccessCentreRouteAccessMode.AllowLegacyUnlessGated
                                                                }
                                                                permission={
                                                                    AccessCentrePermissionKey.CurationsManageDrafts
                                                                }
                                                            >
                                                                <CustomPage />
                                                            </AccessCentreRouteGuard>
                                                        </RouteGuard>
                                                    </Route>
                                                )}
                                                {isAuthenticated && (
                                                    <Route path={URLPaths.CURATIONS_CONDITIONAL_SPLIT}>
                                                        <RouteGuard
                                                            hasPermission={!hasContentOnly}
                                                            isLoading={isContentOnlyLoading}
                                                        >
                                                            <AccessCentreRouteGuard
                                                                accessMode={
                                                                    AccessCentreRouteAccessMode.AllowLegacyUnlessGated
                                                                }
                                                                permission={
                                                                    AccessCentrePermissionKey.CurationsManageDrafts
                                                                }
                                                            >
                                                                <ConditionalSplitPage />
                                                            </AccessCentreRouteGuard>
                                                        </RouteGuard>
                                                    </Route>
                                                )}
                                                {isAuthenticated && (
                                                    <Route path={URLPaths.CURATIONS_SINGLE_SEND}>
                                                        <RouteGuard
                                                            hasPermission={!hasContentOnly}
                                                            isLoading={isContentOnlyLoading}
                                                        >
                                                            <AccessCentreRouteGuard
                                                                accessMode={
                                                                    AccessCentreRouteAccessMode.AllowLegacyUnlessGated
                                                                }
                                                                permission={
                                                                    AccessCentrePermissionKey.CurationsManageDrafts
                                                                }
                                                            >
                                                                <SingleSendPage />
                                                            </AccessCentreRouteGuard>
                                                        </RouteGuard>
                                                    </Route>
                                                )}
                                                {isAuthenticated && (
                                                    <Route path={URLPaths.CURATIONS_EMAIL_SERIES}>
                                                        <RouteGuard
                                                            hasPermission={!hasContentOnly}
                                                            isLoading={isContentOnlyLoading}
                                                        >
                                                            <AccessCentreRouteGuard
                                                                accessMode={
                                                                    AccessCentreRouteAccessMode.AllowLegacyUnlessGated
                                                                }
                                                                permission={
                                                                    AccessCentrePermissionKey.CurationsManageDrafts
                                                                }
                                                            >
                                                                <TimeDelayPage />
                                                            </AccessCentreRouteGuard>
                                                        </RouteGuard>
                                                    </Route>
                                                )}
                                                {isAuthenticated && (
                                                    <Route path={URLPaths.CURATIONS} exact>
                                                        <RouteGuard
                                                            hasPermission={!hasContentOnly}
                                                            isLoading={isContentOnlyLoading}
                                                        >
                                                            <AccessCentreRouteGuard
                                                                accessMode={
                                                                    AccessCentreRouteAccessMode.AllowLegacyUnlessGated
                                                                }
                                                                permission={AccessCentrePermissionKey.CurationsView}
                                                            >
                                                                <CurationsPage />
                                                            </AccessCentreRouteGuard>
                                                        </RouteGuard>
                                                    </Route>
                                                )}
                                                {isAuthenticated && (
                                                    <Route path={`${URLPaths.CURATIONS}/:campaignId/summary`} exact>
                                                        <RouteGuard
                                                            hasPermission={!hasContentOnly}
                                                            isLoading={isContentOnlyLoading}
                                                        >
                                                            <AccessCentreRouteGuard
                                                                accessMode={
                                                                    AccessCentreRouteAccessMode.AllowLegacyUnlessGated
                                                                }
                                                                permission={AccessCentrePermissionKey.CurationsView}
                                                            >
                                                                <CampaignDetailsPageWrapper />
                                                            </AccessCentreRouteGuard>
                                                        </RouteGuard>
                                                    </Route>
                                                )}
                                                {isAuthenticated && (
                                                    <Route path={URLPaths.SEGMENTS} exact>
                                                        <RouteGuard
                                                            hasPermission={!hasContentOnly}
                                                            isLoading={isContentOnlyLoading}
                                                        >
                                                            <AccessCentreRouteGuard
                                                                accessMode={
                                                                    AccessCentreRouteAccessMode.AllowLegacyUnlessGated
                                                                }
                                                                permission={AccessCentrePermissionKey.AudiencesView}
                                                            >
                                                                <SegmentsPage />
                                                            </AccessCentreRouteGuard>
                                                        </RouteGuard>
                                                    </Route>
                                                )}
                                                {isAuthenticated && (
                                                    <Route path={URLPaths.SEGMENTS_ARCHIVED} exact>
                                                        <RouteGuard
                                                            hasPermission={!hasContentOnly}
                                                            isLoading={isContentOnlyLoading}
                                                        >
                                                            <AccessCentreRouteGuard
                                                                accessMode={
                                                                    AccessCentreRouteAccessMode.AllowLegacyUnlessGated
                                                                }
                                                                permission={AccessCentrePermissionKey.AudiencesView}
                                                            >
                                                                <SegmentsPage />
                                                            </AccessCentreRouteGuard>
                                                        </RouteGuard>
                                                    </Route>
                                                )}
                                                {isAuthenticated && (
                                                    <Route path={`${URLPaths.SEGMENTS_FOLDER}/:folderId`} exact>
                                                        <RouteGuard
                                                            hasPermission={!hasContentOnly}
                                                            isLoading={isContentOnlyLoading}
                                                        >
                                                            <AccessCentreRouteGuard
                                                                accessMode={
                                                                    AccessCentreRouteAccessMode.AllowLegacyUnlessGated
                                                                }
                                                                permission={AccessCentrePermissionKey.AudiencesView}
                                                            >
                                                                <SegmentsPage />
                                                            </AccessCentreRouteGuard>
                                                        </RouteGuard>
                                                    </Route>
                                                )}
                                                {isAuthenticated && (
                                                    <Route path={URLPaths.SEGMENT_CREATE} exact>
                                                        <RouteGuard
                                                            hasPermission={!hasContentOnly}
                                                            isLoading={isContentOnlyLoading}
                                                        >
                                                            <AccessCentreRouteGuard
                                                                accessMode={
                                                                    AccessCentreRouteAccessMode.AllowLegacyUnlessGated
                                                                }
                                                                permission={AccessCentrePermissionKey.AudiencesManage}
                                                            >
                                                                {hasAudienceBuilderNested ? (
                                                                    <NestedSegmentCreatePage />
                                                                ) : (
                                                                    <SegmentCreatePage />
                                                                )}
                                                            </AccessCentreRouteGuard>
                                                        </RouteGuard>
                                                    </Route>
                                                )}
                                                {isAuthenticated && (
                                                    <Route path={URLPaths.SEGMENTS_CONTACTS} exact>
                                                        <RouteGuard
                                                            hasPermission={!hasContentOnly}
                                                            isLoading={isContentOnlyLoading}
                                                        >
                                                            <AccessCentreRouteGuard
                                                                accessMode={
                                                                    AccessCentreRouteAccessMode.AllowLegacyUnlessGated
                                                                }
                                                                permissionsAnyOf={[
                                                                    AccessCentrePermissionKey.ContactsView,
                                                                    AccessCentrePermissionKey.ContactsViewPhi,
                                                                ]}
                                                            >
                                                                <ContactsPage />
                                                            </AccessCentreRouteGuard>
                                                        </RouteGuard>
                                                    </Route>
                                                )}
                                                {isAuthenticated && (
                                                    <Route path={URLPaths.CONTACTS} exact>
                                                        <RouteGuard
                                                            hasPermission={!hasContentOnly}
                                                            isLoading={isContentOnlyLoading}
                                                        >
                                                            <AccessCentreRouteGuard
                                                                accessMode={
                                                                    AccessCentreRouteAccessMode.AllowLegacyUnlessGated
                                                                }
                                                                permissionsAnyOf={[
                                                                    AccessCentrePermissionKey.ContactsView,
                                                                    AccessCentrePermissionKey.ContactsViewPhi,
                                                                ]}
                                                            >
                                                                <ContactsPage />
                                                            </AccessCentreRouteGuard>
                                                        </RouteGuard>
                                                    </Route>
                                                )}
                                                {isAuthenticated && (
                                                    <Route path={URLPaths.CONTENT} exact>
                                                        <AccessCentreRouteGuard
                                                            accessMode={
                                                                AccessCentreRouteAccessMode.AllowLegacyUnlessGated
                                                            }
                                                            permission={AccessCentrePermissionKey.ContentView}
                                                        >
                                                            <ContentPage />
                                                        </AccessCentreRouteGuard>
                                                    </Route>
                                                )}
                                                {isAuthenticated && (
                                                    <Route path={URLPaths.CONTENT_ARCHIVED} exact>
                                                        <AccessCentreRouteGuard
                                                            accessMode={
                                                                AccessCentreRouteAccessMode.AllowLegacyUnlessGated
                                                            }
                                                            permission={AccessCentrePermissionKey.ContentView}
                                                        >
                                                            <ContentPage />
                                                        </AccessCentreRouteGuard>
                                                    </Route>
                                                )}
                                                {isAuthenticated && (
                                                    <Route path={`${URLPaths.CONTENT_FOLDER}/:folderId`} exact>
                                                        <AccessCentreRouteGuard
                                                            accessMode={
                                                                AccessCentreRouteAccessMode.AllowLegacyUnlessGated
                                                            }
                                                            permission={AccessCentrePermissionKey.ContentView}
                                                        >
                                                            <ContentPage />
                                                        </AccessCentreRouteGuard>
                                                    </Route>
                                                )}
                                                {isAuthenticated && (
                                                    <Route path={URLPaths.CONTENT_CREATE_DIRECT_MAIL} exact>
                                                        <AccessCentreRouteGuard
                                                            accessMode={
                                                                AccessCentreRouteAccessMode.AllowLegacyUnlessGated
                                                            }
                                                            permission={AccessCentrePermissionKey.ContentView}
                                                        >
                                                            <ContentDirectMailBuilderPage />
                                                        </AccessCentreRouteGuard>
                                                    </Route>
                                                )}
                                                {isAuthenticated && (
                                                    <Route path={URLPaths.CONTENT_CREATE_EMAIL} exact>
                                                        <AccessCentreRouteGuard
                                                            accessMode={
                                                                AccessCentreRouteAccessMode.AllowLegacyUnlessGated
                                                            }
                                                            permission={AccessCentrePermissionKey.ContentView}
                                                        >
                                                            <ContentEmailBuilderPage />
                                                        </AccessCentreRouteGuard>
                                                    </Route>
                                                )}
                                                {isAuthenticated && (
                                                    <Route path={URLPaths.CONTENT_CREATE_PAGE} exact>
                                                        <AccessCentreRouteGuard
                                                            accessMode={
                                                                AccessCentreRouteAccessMode.AllowLegacyUnlessGated
                                                            }
                                                            permission={AccessCentrePermissionKey.ContentView}
                                                        >
                                                            <ContentPageBuilderPage />
                                                        </AccessCentreRouteGuard>
                                                    </Route>
                                                )}
                                                {isAuthenticated && (
                                                    <Route path={URLPaths.CONTENT_CREATE_SMS} exact>
                                                        <AccessCentreRouteGuard
                                                            accessMode={
                                                                AccessCentreRouteAccessMode.AllowLegacyUnlessGated
                                                            }
                                                            permission={AccessCentrePermissionKey.ContentView}
                                                        >
                                                            <ContentSmsBuilderPage />
                                                        </AccessCentreRouteGuard>
                                                    </Route>
                                                )}
                                                {isAuthenticated && (
                                                    <Route path={URLPaths.CONTENT_CREATE_VOICE_CALL} exact>
                                                        <AccessCentreRouteGuard
                                                            accessMode={
                                                                AccessCentreRouteAccessMode.AllowLegacyUnlessGated
                                                            }
                                                            permission={AccessCentrePermissionKey.ContentView}
                                                        >
                                                            <ContentVoiceBuilderPage />
                                                        </AccessCentreRouteGuard>
                                                    </Route>
                                                )}
                                                {isAuthenticated && (
                                                    <Route path={URLPaths.CONTENT_CREATE_WHATSAPP} exact>
                                                        <AccessCentreRouteGuard
                                                            accessMode={
                                                                AccessCentreRouteAccessMode.AllowLegacyUnlessGated
                                                            }
                                                            permission={AccessCentrePermissionKey.ContentView}
                                                        >
                                                            <ContentWhatsAppBuilderPage />
                                                        </AccessCentreRouteGuard>
                                                    </Route>
                                                )}
                                                {isAuthenticated && (
                                                    <Route path={URLPaths.CONTENT_VIEW_DIRECT_MAIL} exact>
                                                        <AccessCentreRouteGuard
                                                            accessMode={
                                                                AccessCentreRouteAccessMode.AllowLegacyUnlessGated
                                                            }
                                                            permission={AccessCentrePermissionKey.ContentView}
                                                        >
                                                            <ContentViewScreen channel={CampaignChannels.DIRECT_MAIL} />
                                                        </AccessCentreRouteGuard>
                                                    </Route>
                                                )}
                                                {isAuthenticated && (
                                                    <Route path={URLPaths.CONTENT_VIEW_EMAIL} exact>
                                                        <AccessCentreRouteGuard
                                                            accessMode={
                                                                AccessCentreRouteAccessMode.AllowLegacyUnlessGated
                                                            }
                                                            permission={AccessCentrePermissionKey.ContentView}
                                                        >
                                                            <ContentViewScreen channel={CampaignChannels.EMAIL} />
                                                        </AccessCentreRouteGuard>
                                                    </Route>
                                                )}
                                                {isAuthenticated && (
                                                    <Route path={URLPaths.CONTENT_VIEW_SMS} exact>
                                                        <AccessCentreRouteGuard
                                                            accessMode={
                                                                AccessCentreRouteAccessMode.AllowLegacyUnlessGated
                                                            }
                                                            permission={AccessCentrePermissionKey.ContentView}
                                                        >
                                                            <ContentViewScreen channel={CampaignChannels.SMS} />
                                                        </AccessCentreRouteGuard>
                                                    </Route>
                                                )}
                                                {isAuthenticated && (
                                                    <Route path={URLPaths.CONTENT_VIEW_VOICE_CALL} exact>
                                                        <AccessCentreRouteGuard
                                                            accessMode={
                                                                AccessCentreRouteAccessMode.AllowLegacyUnlessGated
                                                            }
                                                            permission={AccessCentrePermissionKey.ContentView}
                                                        >
                                                            <ContentViewScreen channel={CampaignChannels.VOICE_CALL} />
                                                        </AccessCentreRouteGuard>
                                                    </Route>
                                                )}
                                                {isAuthenticated && (
                                                    <Route path={URLPaths.CONTENT_VIEW_WHATSAPP} exact>
                                                        <AccessCentreRouteGuard
                                                            accessMode={
                                                                AccessCentreRouteAccessMode.AllowLegacyUnlessGated
                                                            }
                                                            permission={AccessCentrePermissionKey.ContentView}
                                                        >
                                                            <ContentViewScreen channel={CampaignChannels.WHATSAPP} />
                                                        </AccessCentreRouteGuard>
                                                    </Route>
                                                )}
                                                {isAuthenticated && (
                                                    <Route path={URLPaths.CONTENT_IMPORT_TEMPLATE} exact>
                                                        <AccessCentreRouteGuard
                                                            accessMode={
                                                                AccessCentreRouteAccessMode.AllowLegacyUnlessGated
                                                            }
                                                            permission={AccessCentrePermissionKey.ContentView}
                                                        >
                                                            <ImportTemplatePage />
                                                        </AccessCentreRouteGuard>
                                                    </Route>
                                                )}
                                                {isAuthenticated && (
                                                    <Route path={URLPaths.INSIGHTS} exact>
                                                        <RouteGuard
                                                            hasPermission={!hasContentOnly}
                                                            isLoading={isContentOnlyLoading}
                                                        >
                                                            <AccessCentreRouteGuard
                                                                accessMode={
                                                                    AccessCentreRouteAccessMode.AllowLegacyUnlessGated
                                                                }
                                                                permission={
                                                                    AccessCentrePermissionKey.InsightsViewDashboards
                                                                }
                                                            >
                                                                <InsightsPage />
                                                            </AccessCentreRouteGuard>
                                                        </RouteGuard>
                                                    </Route>
                                                )}
                                                {isAuthenticated && (
                                                    <Route path={URLPaths.SETTINGS_USERS} exact>
                                                        <AccessCentreRouteGuard
                                                            accessMode={
                                                                AccessCentreRouteAccessMode.RequireFeatureAndPermission
                                                            }
                                                            permission={AccessCentrePermissionKey.AccessManagementView}
                                                        >
                                                            <UsersPage />
                                                        </AccessCentreRouteGuard>
                                                    </Route>
                                                )}
                                                {isAuthenticated && (
                                                    <Route path={URLPaths.SETTINGS_ROLES}>
                                                        <AccessCentreRouteGuard
                                                            accessMode={
                                                                AccessCentreRouteAccessMode.RequireFeatureAndPermission
                                                            }
                                                            permission={AccessCentrePermissionKey.AccessManagementView}
                                                        >
                                                            <SettingsRolesRoute />
                                                        </AccessCentreRouteGuard>
                                                    </Route>
                                                )}
                                                {isAuthenticated && (
                                                    <Route path={URLPaths.SETTINGS_BRAND}>
                                                        <RouteGuard
                                                            hasPermission={brandSettingsRouteAllowed && !hasContentOnly}
                                                            isLoading={
                                                                brandSettingsRouteLoading || isContentOnlyLoading
                                                            }
                                                        >
                                                            <SettingsBrandRoute />
                                                        </RouteGuard>
                                                    </Route>
                                                )}
                                                {isAuthenticated && (
                                                    <Route path={URLPaths.SETTINGS_AI_GUARDRAILS} exact>
                                                        <RouteGuard
                                                            hasPermission={!!hasAiGuardrailsAdminPanelFlag}
                                                            isLoading={hasAiGuardrailsAdminPanelFlag === undefined}
                                                        >
                                                            <AIGuardrailsPage />
                                                        </RouteGuard>
                                                    </Route>
                                                )}
                                                {isAuthenticated && (
                                                    <Route path={URLPaths.VALUE} exact>
                                                        <RouteGuard
                                                            hasPermission={!hasContentOnly}
                                                            isLoading={isContentOnlyLoading}
                                                        >
                                                            <AccessCentreRouteGuard
                                                                accessMode={
                                                                    AccessCentreRouteAccessMode.AllowLegacyUnlessGated
                                                                }
                                                                permission={AccessCentrePermissionKey.ValueView}
                                                            >
                                                                <ValueCalculatorPage />
                                                            </AccessCentreRouteGuard>
                                                        </RouteGuard>
                                                    </Route>
                                                )}
                                                {isAuthenticated && (
                                                    <Route path={URLPaths.VALUE_CREATE}>
                                                        <RouteGuard
                                                            hasPermission={!hasContentOnly}
                                                            isLoading={isContentOnlyLoading}
                                                        >
                                                            <AccessCentreRouteGuard
                                                                accessMode={
                                                                    AccessCentreRouteAccessMode.AllowLegacyUnlessGated
                                                                }
                                                                permission={AccessCentrePermissionKey.ValueCreate}
                                                            >
                                                                <ValueCalculatorCreatePage />
                                                            </AccessCentreRouteGuard>
                                                        </RouteGuard>
                                                    </Route>
                                                )}
                                                {isAuthenticated && (
                                                    <Route path={`${URLPaths.VALUE_DETAILS}/:sendId/summary`} exact>
                                                        <RouteGuard
                                                            hasPermission={!hasContentOnly}
                                                            isLoading={isContentOnlyLoading}
                                                        >
                                                            <AccessCentreRouteGuard
                                                                accessMode={
                                                                    AccessCentreRouteAccessMode.AllowLegacyUnlessGated
                                                                }
                                                                permission={AccessCentrePermissionKey.ValueView}
                                                            >
                                                                <ValueCalculatorDetailsPage />
                                                            </AccessCentreRouteGuard>
                                                        </RouteGuard>
                                                    </Route>
                                                )}
                                                {isAuthenticated && hasCustomerSupportFlag && (
                                                    <Route path={URLPaths.SETTINGS_CUSTOMER_SUPPORT} exact>
                                                        <RouteGuard
                                                            hasPermission={hasCustomerSupportFlag && !hasContentOnly}
                                                            isLoading={isContentOnlyLoading}
                                                        >
                                                            <CustomerSupportPage />
                                                        </RouteGuard>
                                                    </Route>
                                                )}
                                            </Switch>
                                        </Background>
                                    </div>
                                </Route>
                            </Switch>
                            <ReactQueryDevtools initialIsOpen={false} position="top-right" />
                        </ReactFlowProvider>
                    </LocalizationProviderPro>
                </LocalizationProvider>
            </ThemeProvider>
        </StyledEngineProvider>
    );
};

export default App;
